<app-page-breadcrumb pageTitle="Métricas de Knowledge Base" />

<div class="space-y-6">

  <!-- Header + selector de chatflow -->
  <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
    <div class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 class="text-xl font-semibold text-gray-800 dark:text-white">Métricas de Knowledge Base</h2>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Analiza la tasa de deflexión y los artículos sugeridos por el nodo
          <span class="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">KNOWLEDGE_SUGGESTION</span>
          de tu chatflow.
        </p>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <!-- inc48: se elige el chatflow por su NOMBRE; el id sigue siendo lo que viaja al backend -->
        <app-entity-select
          class="w-64"
          placeholder="Busca un chatflow por su nombre…"
          [(value)]="chatflowId"
          [displayText]="chatflowName"
          [searchFn]="buscarChatflows"
          [labelFn]="etiquetaChatflow"
          [sublabelFn]="subEtiquetaChatflow"
          (selected)="onChatflowSeleccionado($event)" />
        <button
          (click)="cargar()"
          [disabled]="!chatflowId || loading"
          class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 transition flex items-center gap-2">
          @if (loading) {
            <span class="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
            Cargando…
          } @else {
            Cargar métricas
          }
        </button>
      </div>
    </div>
  </div>

  <!-- Error -->
  @if (error && !loading) {
    <div class="rounded-xl border border-error-200 bg-error-50 p-4 flex items-start gap-3 dark:border-error-500/20 dark:bg-error-500/10">
      <svg class="w-5 h-5 text-error-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      </svg>
      <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  }

  <!-- Estado vacío inicial -->
  @if (!loading && !error && !metrics) {
    <div class="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 px-6 py-20 text-center">
      <svg class="w-14 h-14 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2"
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
      </svg>
      <p class="text-base font-medium text-gray-500 dark:text-gray-400">Elige un chatflow para ver sus métricas</p>
      <p class="mt-1 text-sm text-gray-400 dark:text-gray-500">Solo aparecen datos si el chatflow tiene un nodo de Knowledge Suggestion con actividad.</p>
    </div>
  }

  <!-- Métricas cargadas -->
  @if (!loading && metrics) {

    <!-- KPI Cards -->
    <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
      <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <p class="text-xs text-gray-400 uppercase tracking-wide mb-2">Sugerencias totales</p>
        <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ metrics.totalKnowledgeSuggestions }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <p class="text-xs text-gray-400 uppercase tracking-wide mb-2">Tasa de deflexión</p>
        <p class="text-3xl font-bold"
          [ngClass]="metrics.deflectionRate >= 50 ? 'text-emerald-600 dark:text-emerald-400' : 'text-warning-600 dark:text-warning-400'">
          {{ metrics.deflectionRate | number:'1.1-1' }}%
        </p>
        <p class="mt-1 text-[11px] text-gray-400">Visitas que no escalaron a agente</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <p class="text-xs text-gray-400 uppercase tracking-wide mb-2">Resueltos</p>
        <p class="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{{ metrics.totalResolved }}</p>
        <p class="mt-1 text-[11px] text-gray-400">{{ getPct(metrics.totalResolved) }}% del total</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <p class="text-xs text-gray-400 uppercase tracking-wide mb-2">No resueltos</p>
        <p class="text-3xl font-bold text-error-600 dark:text-error-400">{{ metrics.totalNotResolved }}</p>
        <p class="mt-1 text-[11px] text-gray-400">{{ getPct(metrics.totalNotResolved) }}% del total</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <p class="text-xs text-gray-400 uppercase tracking-wide mb-2">Sin resultados</p>
        <p class="text-3xl font-bold text-gray-600 dark:text-gray-400">{{ metrics.totalNoResults }}</p>
        <p class="mt-1 text-[11px] text-gray-400">{{ getPct(metrics.totalNoResults) }}% del total</p>
      </div>
    </div>

    <!-- Barra de distribución -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Distribución de resolución</h3>
      @if (metrics.totalKnowledgeSuggestions === 0) {
        <p class="text-sm text-gray-400 italic">Sin datos suficientes para mostrar la distribución.</p>
      } @else {
        <div class="flex rounded-full overflow-hidden h-6 text-xs font-semibold">
          @if (getPct(metrics.totalResolved) > 0) {
            <div class="bg-emerald-500 flex items-center justify-center text-white transition-all"
                 [style.width]="getPct(metrics.totalResolved) + '%'">
              @if (getPct(metrics.totalResolved) > 12) { {{ getPct(metrics.totalResolved) }}% }
            </div>
          }
          @if (getPct(metrics.totalNotResolved) > 0) {
            <div class="bg-error-400 flex items-center justify-center text-white transition-all"
                 [style.width]="getPct(metrics.totalNotResolved) + '%'">
              @if (getPct(metrics.totalNotResolved) > 12) { {{ getPct(metrics.totalNotResolved) }}% }
            </div>
          }
          @if (getPct(metrics.totalNoResults) > 0) {
            <div class="bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-all"
                 [style.width]="getPct(metrics.totalNoResults) + '%'">
              @if (getPct(metrics.totalNoResults) > 12) { {{ getPct(metrics.totalNoResults) }}% }
            </div>
          }
        </div>
        <div class="flex flex-wrap gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
          <span class="flex items-center gap-1.5">
            <span class="w-3 h-3 rounded-full bg-emerald-500 shrink-0"></span>
            Resueltos ({{ metrics.totalResolved }})
          </span>
          <span class="flex items-center gap-1.5">
            <span class="w-3 h-3 rounded-full bg-error-400 shrink-0"></span>
            No resueltos ({{ metrics.totalNotResolved }})
          </span>
          <span class="flex items-center gap-1.5">
            <span class="w-3 h-3 rounded-full bg-gray-400 shrink-0"></span>
            Sin resultados ({{ metrics.totalNoResults }})
          </span>
        </div>
      }
    </div>

    <!-- Artículos + búsquedas sin resultado -->
    <div class="grid gap-6 md:grid-cols-2">

      <!-- Top artículos sugeridos -->
      <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          📄 Artículos más sugeridos
        </h3>
        @if (metrics.topSuggestedArticles.length === 0) {
          <div class="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 px-4 py-8 text-center">
            <p class="text-sm text-gray-400">Sin artículos sugeridos todavía.</p>
          </div>
        } @else {
          <div class="space-y-1">
            @for (a of metrics.topSuggestedArticles; track a.articleId; let i = $index) {
              <div class="flex items-center gap-3 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <span class="w-5 text-right text-xs font-bold text-gray-400 shrink-0">{{ i + 1 }}</span>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{{ a.title }}</p>
                  <a [href]="'/knowledge/' + a.slug" target="_blank"
                     class="text-[10px] text-brand-500 hover:underline font-mono">
                    /{{ a.slug }}
                  </a>
                </div>
                <span class="shrink-0 text-xs bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-300 px-2 py-0.5 rounded-full font-semibold">
                  {{ a.occurrences }}×
                </span>
              </div>
            }
          </div>
        }
      </div>

      <!-- Búsquedas sin resultado -->
      <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          🔍 Búsquedas sin resultado
        </h3>
        <p class="text-xs text-gray-400 mb-4">Términos que los visitantes buscaron pero no encontraron artículos.</p>
        @if (metrics.topNoResultQueries.length === 0) {
          <div class="rounded-lg border border-dashed border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/5 px-4 py-8 text-center">
            <p class="text-sm font-medium text-emerald-700 dark:text-emerald-400">¡Sin búsquedas vacías!</p>
            <p class="text-xs text-emerald-600 dark:text-emerald-500 mt-1">La base de conocimiento cubre todas las consultas.</p>
          </div>
        } @else {
          <div class="space-y-1">
            @for (q of metrics.topNoResultQueries; track q.searchTerm; let i = $index) {
              <div class="flex items-center gap-3 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <span class="w-5 text-right text-xs font-bold text-gray-400 shrink-0">{{ i + 1 }}</span>
                <p class="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate italic">"{{ q.searchTerm }}"</p>
                <span class="shrink-0 text-xs bg-error-50 dark:bg-error-500/10 text-error-600 dark:text-error-400 px-2 py-0.5 rounded-full font-semibold">
                  {{ q.occurrences }}×
                </span>
              </div>
            }
          </div>
          <p class="mt-4 text-[11px] text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-3">
            💡 Considera crear artículos para estos términos para mejorar la tasa de deflexión.
          </p>
        }
      </div>

    </div>
  }

</div>
